import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, computed, signal } from '@angular/core';
import { ClarityModule } from '@clr/angular';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LOCALE_ID } from '@angular/core';
import { OverlayPanelComponent } from '../../../shared/components/overlay-panel/overlay-panel.component';
import { VoxtralExportReviewComponent } from '../../programplan/components/voxtral-export-review/voxtral-export-review.component';
import { defaultFabUrlForChannel } from '../../programplan/components/voxtral-fab-defaults';
import { BroadcastStatisticsService } from '../broadcast-statistics.service';
import { StatisticsRange, VoxtralBroadcastStatistics, VoxtralBroadcastSummary } from '../../../models/broadcast-statistic';
import { downloadCsv } from '../../../shared/utils/csv-export';

/**
 * Voxtral-Tab der Statistikseite.
 *
 * Die Zeilenlogik folgt scripts/export_voxtral_broadcasts.sh (gegen die
 * Juli-2026-PRD-Daten verifiziert): transkribiert = Session gestartet ODER
 * persistierter Export; "an FAB gesendet" = FabEnabled. Zusätzlich stehen
 * die nur geplanten Sendungen in der Liste, damit Ausfälle nicht unsichtbar
 * aus dem Grid fallen.
 *
 * Der CSV-Export beginnt mit exakt den Spalten des Skripts (gleiche
 * Reihenfolge), damit sich beide Exporte direkt vergleichen lassen.
 */
@Component({
  selector: 'app-voxtral-statistics',
  standalone: true,
  imports: [CommonModule, FormsModule, ClarityModule, OverlayPanelComponent, VoxtralExportReviewComponent],
  providers: [{ provide: LOCALE_ID, useValue: 'de-DE' }],
  templateUrl: './voxtral-statistics.component.html',
  styleUrl: './voxtral-statistics.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class VoxtralStatisticsComponent implements OnChanges {
  /** Zeitraum + Sender; neue Objektidentität = neu laden. */
  @Input() range: StatisticsRange | null = null;
  /** Nur der sichtbare Tab lädt — sonst zahlt jeder Tabwechsel doppelt. */
  @Input() active = false;

  private readonly summaries = signal<VoxtralBroadcastSummary[]>([]);
  readonly broadcastList = computed(() => this.summaries());

  statistics: VoxtralBroadcastStatistics = {} as VoxtralBroadcastStatistics;
  isLoading = false;

  /** Für das Detail-Overlay (Segmente/Korrekturen der Session). */
  selectedBroadcast: VoxtralBroadcastSummary | null = null;

  private loadedRange: StatisticsRange | null = null;

  constructor(private statsService: BroadcastStatisticsService) {}

  ngOnChanges(_changes: SimpleChanges): void {
    this.loadIfNeeded();
  }

  private loadIfNeeded(): void {
    if (!this.active || !this.range || this.range === this.loadedRange) {
      return;
    }

    const range = this.range;
    this.loadedRange = range;

    if (!range.channelFilter) {
      this.statistics = {} as VoxtralBroadcastStatistics;
      this.summaries.set([]);
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.statsService.getVoxtralStatistics(range.fromDate, range.toDateExclusive, range.channelFilter)
      .subscribe({
        next: (data) => {
          this.summaries.set(data?.broadcasts ?? []);
          this.statistics = data ?? ({} as VoxtralBroadcastStatistics);
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Fehler beim Laden der Voxtral-Statistiken', err);
          // Range freigeben, damit ein Tabwechsel es erneut versucht.
          this.loadedRange = null;
          this.isLoading = false;
        }
      });
  }

  /** "Geplant, nie gestartet" zuerst — solche Zeilen haben nie einen Export. */
  statusLabel(broadcast: VoxtralBroadcastSummary): string {
    if (broadcast.scheduledNeverRan) return 'Geplant, nie gestartet';
    if (broadcast.stalledSince) return 'Stalled';
    if (!broadcast.hasExport || broadcast.segmentsCount === 0) return 'Ohne Export';
    return 'Gelaufen';
  }

  statusClass(broadcast: VoxtralBroadcastSummary): string {
    if (broadcast.scheduledNeverRan) return 'label-warning';
    if (broadcast.stalledSince) return 'label-danger';
    if (!broadcast.hasExport || broadcast.segmentsCount === 0) return 'label-danger';
    return 'label-success';
  }

  /**
   * Ohne gespeicherte FAB-URL fällt die Session auf den Sender-Default
   * zurück; das wird als "(Standard)" markiert, damit die Anzeige nicht wie
   * eine bewusst gesetzte Adresse aussieht. Der CSV-Export bleibt beim
   * gespeicherten Rohwert (Skript-Parität).
   */
  displayFabUrl(broadcast: VoxtralBroadcastSummary): string {
    if (broadcast.fabUrl) return broadcast.fabUrl;
    return `${defaultFabUrlForChannel(broadcast.channel)} (Standard)`;
  }

  openDetails(broadcast: VoxtralBroadcastSummary): void {
    this.selectedBroadcast = broadcast;
  }

  closeDetails(): void {
    this.selectedBroadcast = null;
  }

  exportToCsv() {
    if (!this.range) {
      return;
    }

    // Die ersten acht Spalten sind identisch mit
    // scripts/export_voxtral_broadcasts.sh; danach zwei Zusatzspalten.
    const headers = [
      'BroadcastKey',
      'ProgrammDate',
      'Channel',
      'Title',
      'SentToFab',
      'FabUrl',
      'SegmentsCount',
      'TranscriptS3Url',
      'SessionStartedAt',
      'Status'
    ];

    const rows = this.broadcastList().map(broadcast => [
      broadcast.key,
      broadcast.programmDate,
      broadcast.channel ?? '',
      broadcast.title ?? '',
      String(broadcast.sentToFab),
      broadcast.fabUrl ?? '',
      broadcast.segmentsCount,
      broadcast.transcriptS3Url ?? '',
      broadcast.sessionStartedAt ?? '',
      this.statusLabel(broadcast)
    ]);

    const from = this.range.fromDate.toISOString().split('T')[0];
    const to = this.range.toDate.toISOString().split('T')[0];
    downloadCsv(`Voxtral_Statistiken_${from}_${to}.csv`, headers, rows);
  }
}
